/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { updateAgency } from '@services/requests/housien-services/AgenciesService.core';

/** The subset of agency fields the profile drawers may patch. */
export type AgencyProfilePatch = Parameters<typeof updateAgency>[1];

/**
 * Saves a partial agency patch, mirrors it into the agency store, and
 * reports the outcome — the flow every profile drawer shares.
 *
 * @param onSuccess - Invoked after a successful save, typically to close the drawer.
 * @returns The submit handler to hand to `handleSubmit`.
 */
export function useSaveAgencyProfile(onSuccess: () => void) {
  const { t } = useTranslation('features/agency-settings');
  const { agency, setAgency } = useAgency((s) => s);

  return useCallback(
    async (data: AgencyProfilePatch) => {
      if (!agency?.id) return;
      try {
        await updateAgency(agency.id, data);
        setAgency({ ...agency, ...data });
        toast.success(t('v2.profile.saveSuccess'));
        onSuccess();
      } catch {
        toast.error(t('v2.profile.saveError'));
      }
    },
    [agency, setAgency, t, onSuccess],
  );
}
